/**
 * =====================================================
 * AVA CLIENT - Home V8
 * =====================================================
 * Reestructurado: Modelo usuario-empresa.
 * - PJ: gestión de empresas + selector al crear solicitud
 * - PH: flujo directo (crea client automáticamente)
 * - Sección de empresas visible para PJ
 * =====================================================
 */
import { useState, useEffect, Fragment } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Shield,
  Loader2,
  ArrowRight,
  LogIn,
  FileText,
  Clock,
  Plus,
  Eye,
  FileSearch,
  Building2,
  AlertTriangle,
  Check,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { getUserErrorMessage } from "@/lib/userErrorMessage";
import { PH_SKIP_DOCS_STEP } from "@/lib/onboardingFlowFlags";
import { useAuth } from "@/_core/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, MonoText, EmptyState, type StatusTone, requestStatusToTone } from "@ava/shared/components/design-system";
import { AppHeader } from "@/components/AppHeader";
import { CompanyManager } from "@/components/CompanyManager";
import { PublicLanding } from "@/pages/home/PublicLanding";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

// Sprint Lifecycle v2: `DOCUMENTS_PENDING` fue removido del enum del backend.
// El request permanece en `PROCESSING` durante toda la fase de revisión por
// compliance, incluso si hay documentos rechazados pendientes de reemplazo.
const ACTIVE_STATUSES = ["DRAFT", "SUBMITTED", "PROCESSING"];
// Estados finales (solo lectura)
const FINAL_STATUSES = ["COMPLETED", "REJECTED", "AUTO_REJECTED", "EXPIRED"];

/**
 * Determina a qué página redirigir según el estado del trámite
 *
 * Sprint Lifecycle v2: el cliente puede entrar a `/documents` cuando
 * `status === "PROCESSING"` y hay docs `REJECTED` para reemplazar o
 * additional_document_requests `PENDING` para subir. El resto del slot
 * set queda read-only — la lógica fina vive en `Documents.tsx`.
 */
function getRedirectPath(request: any): { path: string; readOnly: boolean; stepName: string } {
  const status = request.status;

  if (FINAL_STATUSES.includes(status)) {
    return { path: `/onboarding/${request.requestId}/analyze`, readOnly: true, stepName: "Estado de solicitud" };
  }
  if (status === "SUBMITTED") {
    if (request.hasPendingAdditionalDocs) {
      return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Documentos adicionales pendientes" };
    }
    if (!request.allInitialDocsComplete) {
      return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Documento requerido pendiente de carga" };
    }
    return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Ver estado de solicitud" };
  }
  if (status === "PROCESSING") {
    // Si hay docs rechazados, llevamos al cliente a `/documents` para
    // que pueda subir reemplazos. Mantenemos el detalle en `analyze` para
    // visibilidad del estado general.
    if (request.hasDocsNeedingAction) {
      return { path: `/onboarding/${request.requestId}/documents`, readOnly: false, stepName: "Reemplazar documentos rechazados" };
    }
    if (request.hasPendingAdditionalDocs) {
      return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Documentos adicionales pendientes" };
    }
    if (!request.allInitialDocsComplete) {
      return { path: `/onboarding/${request.requestId}/analyze`, readOnly: false, stepName: "Documento requerido pendiente de carga" };
    }
    return { path: `/onboarding/${request.requestId}/analyze`, readOnly: true, stepName: "Estado de solicitud" };
  }
  if (status === "DRAFT") {
    // TALAPP-1767 (PH_SKIP_DOCS_STEP): en el tramo inicial PH retoma en
    // /review (saltea la carga de documentos); PJ sigue en /documents.
    if (PH_SKIP_DOCS_STEP && request.customerType === "PERSONA_HUMANA") {
      return { path: `/onboarding/${request.requestId}/review`, readOnly: false, stepName: "Revisión de datos" };
    }
    return { path: `/onboarding/${request.requestId}/documents`, readOnly: false, stepName: "Carga de documentos" };
  }
  return { path: `/onboarding/${request.requestId}/documents`, readOnly: false, stepName: "Continuar trámite" };
}

// Mapea status del backend a tone del Design System.
// Mantenemos `barColor` con clases Tailwind porque el progress bar (l.481)
// usa un fondo sólido custom, no aplica el patrón bg-50/text-700 del badge.
function getStatusConfig(status: string): { label: string; tone: StatusTone; barColor: string } {
  switch (status) {
    case "DRAFT":
      return { label: "Borrador", tone: "neutral", barColor: "bg-gray-400" };
    case "SUBMITTED":
      return { label: "Enviado", tone: "info", barColor: "bg-blue-400" };
    case "PROCESSING":
      return { label: "En proceso", tone: "info", barColor: "bg-blue-500" };
    case "COMPLETED":
      return { label: "Finalizado", tone: "success", barColor: "bg-green-500" };
    case "REJECTED":
      return { label: "Finalizado", tone: "danger", barColor: "bg-red-500" };
    case "AUTO_REJECTED":
      return { label: "Finalizado", tone: "danger", barColor: "bg-red-500" };
    case "EXPIRED":
      return { label: "Finalizado", tone: "neutral", barColor: "bg-gray-400" };
    default:
      return { label: status, tone: requestStatusToTone(status), barColor: "bg-gray-400" };
  }
}

type VerificationStatus = "sin_verificar" | "sin_cliente" | "pendiente" | "procesando" | "aprobada" | "rechazada" | "revision_manual" | null;
interface VerificationData {
  status: VerificationStatus;
  documentId?: number;
  hasDocument: boolean;
}

interface CompactRequestStepperProps {
  steps: Array<{ label: string; done: boolean }>;
  currentStepIndex: number;
  activeRequest: any;
  redirectStepName: string;
}

function CompactRequestStepper({
  steps,
  currentStepIndex,
  activeRequest,
  redirectStepName,
}: CompactRequestStepperProps) {
  return (
    <>
      <div className="flex items-center mt-3 mb-2">
        {steps.map((step, i) => {
          const isCompleted = i < currentStepIndex;
          const isActive = i === currentStepIndex;
          return (
            <Fragment key={i}>
              <div
                className={`w-[22px] h-[22px] rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-all ${
                  isCompleted
                    ? "text-white"
                    : isActive
                      ? "bg-white border-2 border-blue-500 text-blue-700"
                      : "bg-slate-100 text-slate-400"
                }`}
                // TALAPP-1767: paso completado en verde de éxito del DS
                // (--ava-success-500 = rgb(34,197,94)), token, no hex.
                // success = completado/aprobado.
                style={isCompleted ? { backgroundColor: "var(--ava-success-500)" } : undefined}
              >
                {isCompleted ? (
                  <Check className="w-3 h-3 stroke-[3]" />
                ) : (
                  i + 1
                )}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-1 transition-all ${
                    i < currentStepIndex ? "" : "bg-slate-200"
                  }`}
                  style={i < currentStepIndex ? { backgroundColor: "var(--ava-success-500)" } : undefined}
                />
              )}
            </Fragment>
          );
        })}
      </div>
      <p className="text-[12.5px] text-left mt-2 mb-3 text-slate-800">
        Paso {currentStepIndex + 1} de {steps.length} ·{" "}
        <span className="font-semibold text-blue-700">
          {steps[currentStepIndex]?.label ?? ""}
        </span>{" "}
        <span className="hidden md:inline">
          {" "}·{" "}
          <span className="text-muted-foreground">
            {activeRequest.hasDocsNeedingAction
              ? "reemplazar documentos rechazados"
              : !activeRequest.allInitialDocsComplete
                ? "documento requerido pendiente de carga"
                : redirectStepName.toLowerCase()}
          </span>
        </span>
      </p>
    </>
  );
}

export default function Home() {
  const [, setLocation] = useLocation();
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const { user, loading: authLoading } = useAuth();
  const [showNewRequestDialog, setShowNewRequestDialog] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [openCompanyCreate, setOpenCompanyCreate] = useState(false);

  // Verificar estado de verificación de identidad
  useEffect(() => {
    async function checkVerification() {
      if (!user) return;
      setVerificationLoading(true);
      try {
        const response = await fetch("/api/verificacion/mi-estado");
        if (response.ok) {
          const data = await response.json();
          setVerificationData({ status: data.status, documentId: data.documentId, hasDocument: data.documentId != null });
        }
      } catch (err: unknown) {
        // Error silenciado
      } finally {
        setVerificationLoading(false);
      }
    }
    checkVerification();
  }, [user]);

  const verificationStatus = verificationData?.status ?? null;

  const { data: userRequests, isLoading: requestsLoading } = trpc.onboarding.list.useQuery(undefined, {
    enabled: !!user,
    retry: 1,
  });

  // Obtener el tipo de persona del cliente registrado
  const { data: myClient, isLoading: clientLoading } = trpc.auth.myClient.useQuery(undefined, {
    enabled: !!user,
    retry: 2,
  });
  const userClientType = myClient?.clientType ?? null;
  const isPJ = userClientType === "PERSONA_JURIDICA";

  // TALAPP-1873: el estado de verificación del OPERADOR aplica a PH y PJ por
  // igual (gates server-side); la pantalla acompaña deshabilitando los CTA y
  // mostrando el banner apenas entra al dashboard.
  const identityQuery = trpc.onboarding.getIdentityStatus.useQuery(
    {},
    { enabled: !!user }
  );
  const needsVerification = !identityQuery.data?.isValid;
  const identityExpired = identityQuery.data?.liveState === "EXPIRED";
  // TALAPP-1876: la renovación de una verificación vencida es PRESENCE (solo
  // selfie) salvo excepciones (DNI físico vencido → FULL); el copy acompaña.
  const identityRenewsWithSelfie = identityExpired && identityQuery.data?.nextMode === "presence";
  // TALAPP-1891: cuenta que nunca verificó pero cuya persona ya está verificada
  // (p. ej. la cuenta PJ de un titular PH): la verificación pedida es PRESENCE
  // (solo selfie), no "DNI + selfie". `nextMode` lo decide el server por persona.
  const identityFirstTimeWithSelfie = !identityExpired && identityQuery.data?.nextMode === "presence";
  // TALAPP-1874: el hero solo cambia de copy con la query RESUELTA — mientras
  // carga, `needsVerification` es true por defecto y mostraría el copy de
  // verificación a un PJ ya verificado (flicker contradictorio).
  const identityStatusReady = !!identityQuery.data;

  // Para PJ: obtener empresas
  const { data: companies } = trpc.auth.myCompanies.useQuery(undefined, {
    enabled: !!user && isPJ,
  });

  const createMutation = trpc.onboarding.create.useMutation({
    onSuccess: (data) => {
      toast.success("Solicitud creada exitosamente");
      setShowNewRequestDialog(false);
      setSelectedCompanyId(null);
      // TALAPP-1767 (PH_SKIP_DOCS_STEP): PH entra directo a /review en la
      // generación inicial; PJ (y cualquier otro tipo) sigue por /documents.
      const initialPath = PH_SKIP_DOCS_STEP && !isPJ ? "review" : "documents";
      setLocation(`/onboarding/${data.requestId}/${initialPath}`);
    },
    onError: (error) => {
      toast.error("Error al crear solicitud: " + getUserErrorMessage(error));
    },
  });

  const handleCreateRequest = () => {
    // TALAPP-1873: mismo freno de pantalla para PH y PJ (el freno real es el
    // gate server-side; esto evita abrir un flujo que va a terminar en 403).
    if (needsVerification) {
      toast.error("Debe verificar su identidad antes de crear una solicitud.");
      return;
    }
    if (isPJ) {
      // PJ: abrir dialog para seleccionar empresa
      setShowNewRequestDialog(true);
    } else {
      // PH: crear solicitud con client existente (creado por AVA-Identity callback)
      if (!myClient?.id) {
        toast.error("Debe verificar su identidad antes de crear una solicitud.");
        return;
      }
      createMutation.mutate({ clientId: myClient.id });
    }
  };

  const handleCreateRequestWithCompany = () => {
    if (!selectedCompanyId) {
      toast.error("Seleccioná una empresa para continuar");
      return;
    }
    createMutation.mutate({ clientId: selectedCompanyId });
  };

  const handleViewRequest = (request: any) => {
    const redirect = getRedirectPath(request);
    setLocation(redirect.path);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  // Separate active and completed requests
  const activeRequests = userRequests?.filter((r: any) => ACTIVE_STATUSES.includes(r.status)) || [];
  const completedRequests = userRequests?.filter((r: any) => FINAL_STATUSES.includes(r.status)) || [];
  const activeRequest = activeRequests[0]; // Primary active request

  // ============================================
  // NOT AUTHENTICATED - Landing page
  // ============================================
  if (!user) {
    return <PublicLanding />;
  }

  // ============================================
  // AUTHENTICATED - Dashboard
  // ============================================
  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--ava-bg, #f8fafc)" }}>
      <AppHeader />

      {/* Hero banner — fondo claro (unificado con mobile) */}
      <div className="relative overflow-hidden">
        <div className="relative z-10 container mx-auto px-4 sm:px-6 py-4 md:py-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="w-full lg:w-auto">
              <h1 className="text-2xl font-bold text-slate-900">
                Hola, {user.name?.split(" ")[0] || "Solicitante"}
              </h1>
              <p className="text-slate-500 mt-1">
                {/* TALAPP-1874: PJ sin verificación vigente — el hero acompaña al
                    banner en vez de contradecirlo ("Registrá tu primera empresa"
                    con el CTA deshabilitado). Decisión 2026-09-02: copy contextual
                    + CTAs bloqueados, SIN redirect ni popup; el freno real son los
                    gates server-side (T3). PH queda como hoy. */}
                {isPJ && identityStatusReady && needsVerification
                  ? (identityExpired
                    ? (identityRenewsWithSelfie
                      ? "Tu verificación de identidad venció. Renovala con una selfie."
                      : "Tu verificación de identidad venció. Renovala para continuar con el proceso de solicitud.")
                    : "Para empezar, primero verificá tu identidad. Después vas a poder continuar con el proceso de solicitud.")
                  : activeRequest
                    ? "Continuá con tu solicitud de onboarding."
                    : isPJ
                      ? (companies && companies.length > 0
                        ? "Seleccioná una empresa para iniciar el trámite."
                        : "Registrá tu primera empresa para comenzar.")
                      : "Iniciá un nuevo trámite de alta."}
              </p>
            </div>
            {/* PH: botón Nueva solicitud */}
            {!activeRequest && !isPJ && (
              <>
                {/* Desktop: botón azul primary (hero claro) */}
                <Button
                  className="hidden lg:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-medium gap-2"
                  style={{ height: "var(--ava-control-h-lg, 44px)" }}
                  onClick={handleCreateRequest}
                  disabled={createMutation.isPending || clientLoading || needsVerification}
                >
                  {createMutation.isPending || clientLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {createMutation.isPending ? "Creando..." : "Cargando..."}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Nueva solicitud
                    </>
                  )}
                </Button>
                {/* Mobile: botón azul primario */}
                <Button
                  className="inline-flex lg:hidden w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium gap-2 mt-4"
                  style={{ height: "var(--ava-control-h-lg, 44px)" }}
                  onClick={handleCreateRequest}
                  disabled={createMutation.isPending || clientLoading || needsVerification}
                >
                  {createMutation.isPending || clientLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {createMutation.isPending ? "Creando..." : "Cargando..."}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Nueva solicitud
                    </>
                  )}
                </Button>
              </>
            )}

            {/* PJ con empresas → "Nueva solicitud" */}
            {isPJ && companies && companies.length > 0 && (
              <>
                {/* Desktop: botón azul primary (hero claro) */}
                <Button
                  className="hidden lg:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-medium gap-2"
                  style={{ height: "var(--ava-control-h-lg, 44px)" }}
                  onClick={handleCreateRequest}
                  disabled={createMutation.isPending || needsVerification}
                >
                  <Plus className="w-4 h-4" />
                  Nueva solicitud
                </Button>
                {/* Mobile: botón azul primario */}
                <Button
                  className="inline-flex lg:hidden w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium gap-2 mt-4"
                  style={{ height: "var(--ava-control-h-lg, 44px)" }}
                  onClick={handleCreateRequest}
                  disabled={createMutation.isPending || needsVerification}
                >
                  <Plus className="w-4 h-4" />
                  Nueva solicitud
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* TALAPP-1873: verificación del operador requerida (PH y PJ) — tokens AVA warn.
            TALAPP-1891: se espera la query resuelta (identityStatusReady), como el
            hero PJ — sin eso `needsVerification` es true mientras carga y el banner
            parpadea en cada recarga de una cuenta ya verificada. */}
        {needsVerification && identityStatusReady && user && (
          <div className="mb-6">
            {/* Desktop Banner */}
            <div
              className="hidden md:block rounded-xl p-6 border"
              style={{
                backgroundColor: "var(--ava-warn-50)",
                borderColor: "var(--ava-warn-200)",
              }}
            >
              <div className="flex items-start gap-3">
                <Shield
                  className="h-6 w-6 shrink-0 mt-0.5"
                  style={{ color: "var(--ava-warn-700)" }}
                />
                <div className="flex-1">
                  <h3 className="font-semibold mb-1" style={{ color: "var(--ava-warn-700)" }}>
                    {identityExpired ? "Tu verificación de identidad venció" : "Verificación de identidad requerida"}
                  </h3>
                  <p className="text-sm mb-4" style={{ color: "var(--ava-warn-700)" }}>
                    {identityExpired
                      ? (identityRenewsWithSelfie ? "Renovala con una selfie." : "Para seguir operando, necesitás renovarla.")
                      : identityFirstTimeWithSelfie
                        ? "Tu documento ya está verificado: para operar con esta cuenta solo necesitás una selfie."
                        : "Para iniciar un trámite de onboarding, primero debe verificar su identidad con una foto de su documento y una selfie."}
                  </p>
                  <Button
                    onClick={() => setLocation("/identity-verification")}
                    className="font-medium"
                    style={{ height: "var(--ava-control-h-lg, 44px)" }}
                  >
                    {identityExpired ? "Renovar verificación" : "Verificar mi identidad"}
                  </Button>
                </div>
              </div>
            </div>

            {/* Mobile Compact Verification Banner */}
            <div className="md:hidden">
              <div
                className="border rounded-xl p-3.5 flex items-center gap-3"
                style={{
                  backgroundColor: "var(--ava-warn-50)",
                  borderColor: "var(--ava-warn-200)",
                }}
              >
                <span 
                  className="w-9 h-9 rounded-lg bg-white border flex items-center justify-center shrink-0"
                  style={{ borderColor: "var(--ava-warn-200)", color: "var(--ava-warn-700)" }}
                >
                  <Shield className="w-[18px] h-[18px]" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-left" style={{ color: "var(--ava-warn-700)", lineHeight: 1.25 }}>
                    {identityExpired ? "Tu verificación venció" : "Verificá tu identidad"}
                  </span>
                  <span className="block text-xs text-left mt-0.5" style={{ color: "var(--ava-warn-700)", opacity: 0.85 }}>
                    {identityExpired
                      ? (identityRenewsWithSelfie ? "Renovala con una selfie" : "Necesitás renovarla")
                      : identityFirstTimeWithSelfie
                        ? "Solo una selfie · 1 min"
                        : "DNI + selfie · 2 min"}
                  </span>
                </span>
                <Button
                  onClick={() => setLocation("/identity-verification")}
                  className="font-semibold shrink-0"
                  style={{ height: "var(--ava-control-h-lg, 44px)" }}
                >
                  {identityExpired ? "Renovar" : "Verificar"}
                </Button>
              </div>
            </div>
          </div>
        )}


        {/* ============================================ */}
        {/* SECCIÓN EMPRESAS - Solo para PJ */}
        {/* ============================================ */}
        {isPJ && (
          <CompanyManager
            onSelectCompany={undefined}
            openCreate={openCompanyCreate}
            onOpenCreateChange={setOpenCompanyCreate}
            createDisabled={needsVerification}
          />
        )}

        {/* Loading */}
        {requestsLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse border rounded-lg p-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                  <div className="w-20 h-6 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Active request - prominent card */}
        {!requestsLoading && activeRequest && (() => {
          const redirect = getRedirectPath(activeRequest);
          const statusConfig = getStatusConfig(activeRequest.status);

          // Determine step progress — PJ includes Beneficiarios/Verificación, PH does not.
          // Sprint Lifecycle v2: stepper agrega "En proceso" (PROCESSING) y
          // "Finalizado" (terminales) al final. Cada etapa se marca `done`
          // cuando el request alcanzó (o superó) ese hito.
          const isPJ = activeRequest.customerType === "PERSONA_JURIDICA";
          const isFinal = FINAL_STATUSES.includes(activeRequest.status);
          const isSubmittedOrLater =
            activeRequest.status === "SUBMITTED" ||
            activeRequest.status === "PROCESSING" ||
            isFinal;
          const isProcessingOrLater =
            activeRequest.status === "PROCESSING" || isFinal;
          const steps = isPJ
            ? [
                { label: "Documentos", done: activeRequest.status !== "DRAFT" },
                { label: "Revisión", done: activeRequest.allInitialDocsComplete || false },
                { label: "Beneficiarios y apoderados", done: activeRequest.hasAnalysis || false },
                { label: "Verificación", done: isSubmittedOrLater },
                { label: "Enviado", done: isSubmittedOrLater },
                { label: "En proceso", done: isProcessingOrLater },
                { label: "Finalizado", done: isFinal },
              ]
            : [
                // TALAPP-1767 (PH_SKIP_DOCS_STEP): PH ya no muestra el paso
                // "Documentos" en el tramo inicial → Revisión → Enviado.
                ...(PH_SKIP_DOCS_STEP
                  ? []
                  : [{ label: "Documentos", done: activeRequest.status !== "DRAFT" }]),
                { label: "Revisión", done: activeRequest.allInitialDocsComplete || false },
                { label: "Enviado", done: isSubmittedOrLater },
                { label: "En proceso", done: isProcessingOrLater },
                { label: "Finalizado", done: isFinal },
              ];
          const completedSteps = steps.filter((s) => s.done).length;
          const progressPercent = Math.round((completedSteps / steps.length) * 100);

          // Sprint Lifecycle v2: banner específico cuando el ejecutivo
          // rechazó documentos y el cliente debe reemplazarlos.
          const showRejectedDocsBanner =
            activeRequest.status === "PROCESSING" &&
            !!(activeRequest as any).hasDocsNeedingAction;
          const rejectedDocsCount =
            (activeRequest as any).actionableDocCount ?? 0;

          return (
            <div className="space-y-2">
              <h3 className="ava-overline">Solicitud activa</h3>

              {/* Sprint Lifecycle v2: banner amber para PROCESSING con docs
                  rechazados. El cliente puede subir reemplazos en `/documents`
                  mientras el ejecutivo sigue revisando los demás docs. */}
              {showRejectedDocsBanner && (
                <div
                  role="status"
                  aria-live="polite"
                  className="rounded-lg border p-4 flex items-start gap-3"
                  style={{
                    backgroundColor: "var(--ava-warn-50)",
                    borderColor: "var(--ava-warn-200)",
                  }}
                >
                  <AlertTriangle
                    className="w-5 h-5 mt-0.5 flex-shrink-0"
                    style={{ color: "var(--ava-warn-700)" }}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-semibold"
                      style={{ color: "var(--ava-warn-700)" }}
                    >
                      {rejectedDocsCount === 1
                        ? "El ejecutivo rechazó 1 documento."
                        : `El ejecutivo rechazó ${rejectedDocsCount} documentos.`}
                    </p>
                    <p
                      className="text-xs mt-1"
                      style={{ color: "var(--ava-warn-700)" }}
                    >
                      Revisá los detalles y subí los reemplazos.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() =>
                      setLocation(`/onboarding/${activeRequest.requestId}/documents`)
                    }
                    className="font-medium"
                  >
                    Ir a documentos
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}

              {/* Desktop Active Request Card (or PJ Card) */}
              <div className={!isPJ ? "hidden md:block" : ""}>
                <Card className="border shadow-sm">
                  <CardContent className="py-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <MonoText className="text-sm text-primary">{activeRequest.requestId}</MonoText>
                          <StatusBadge tone={statusConfig.tone}>{statusConfig.label}</StatusBadge>
                          <StatusBadge tone="info">
                            {activeRequest.customerType === "PERSONA_JURIDICA" ? "PJ" : "PH"}
                          </StatusBadge>
                          {(activeRequest as any).hasDocsNeedingAction && (
                            <StatusBadge tone="danger">
                              {(activeRequest as any).actionableDocCount === 1
                                ? "1 documento rechazado"
                                : `${(activeRequest as any).actionableDocCount} documentos rechazados`}
                            </StatusBadge>
                          )}
                          {(activeRequest as any).hasUnconfirmedDocs &&
                            (activeRequest.status === "DRAFT" || activeRequest.status === "PROCESSING") && (
                            <StatusBadge tone="warn">
                              {(activeRequest as any).unconfirmedDocCount === 1
                                ? "1 documento por confirmar"
                                : `${(activeRequest as any).unconfirmedDocCount} documentos por confirmar`}
                            </StatusBadge>
                          )}
                          {(activeRequest as any).hasPendingAdditionalDocs && (
                            <StatusBadge tone="warn">
                              Documentos adicionales requeridos
                            </StatusBadge>
                          )}
                          {!(activeRequest as any).allInitialDocsComplete &&
                            !(activeRequest as any).hasPendingAdditionalDocs &&
                            !(activeRequest as any).hasDocsNeedingAction && (
                            <StatusBadge tone="warn">
                              Documento requerido pendiente
                            </StatusBadge>
                          )}
                        </div>
                        <h3 className="ava-h3">
                          {activeRequest.clientName || (activeRequest as any).entityName || (activeRequest.customerType === "PERSONA_JURIDICA" ? "Persona Jurídica" : "Persona Humana")}
                        </h3>
                        {activeRequest.clientIdentifier && (
                          <p className="text-xs text-muted-foreground">CUIT: <MonoText>{activeRequest.clientIdentifier}</MonoText></p>
                        )}
                        <p className="text-sm text-muted-foreground mt-1">
                          Próximo paso:{" "}
                          <span className="font-medium text-foreground">
                            {(activeRequest as any).hasDocsNeedingAction
                              ? "Reemplazar documentos rechazados"
                              : !(activeRequest as any).allInitialDocsComplete
                                ? "Documento requerido pendiente de carga"
                                : redirect.stepName}
                          </span>
                        </p>
                      </div>
                      <Button onClick={() => handleViewRequest(activeRequest)} className="font-semibold w-full sm:w-auto">
                        Continuar
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>

                    {/* Step progress (V3 Stepper) */}
                    <div className="mt-4 border-t border-slate-100 pt-3">
                      {(() => {
                        const cur = steps.findIndex((s) => !s.done);
                        const currentStepIndex = cur === -1 ? steps.length - 1 : cur;
                        return (
                          <CompactRequestStepper
                            steps={steps}
                            currentStepIndex={currentStepIndex}
                            activeRequest={activeRequest}
                            redirectStepName={redirect.stepName}
                          />
                        );
                      })()}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Mobile PH Active Request Card (V3) */}
              {!isPJ && (
                <div className="md:hidden">
                  <div className="border border-slate-200 rounded-xl bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1 mr-2">
                        <h3 className="font-bold text-[16px] text-left text-slate-900 truncate">
                          {activeRequest.clientName || "Persona Humana"}
                        </h3>
                        <MonoText className="text-xs text-muted-foreground">{activeRequest.requestId}</MonoText>
                      </div>
                      <StatusBadge tone={statusConfig.tone}>{statusConfig.label}</StatusBadge>
                    </div>
                    {/* Stepper of circles + connectors (V3) */}
                    {(() => {
                      const cur = steps.findIndex((s) => !s.done);
                      const currentStepIndex = cur === -1 ? steps.length - 1 : cur;
                      return (
                        <CompactRequestStepper
                          steps={steps}
                          currentStepIndex={currentStepIndex}
                          activeRequest={activeRequest}
                          redirectStepName={redirect.stepName}
                        />
                      );
                    })()}
                    <Button
                      onClick={() => handleViewRequest(activeRequest)}
                      className="font-semibold w-full mt-3.5 flex items-center justify-center gap-2"
                      style={{ height: "var(--ava-control-h-lg, 44px)" }}
                    >
                      Continuar
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Other active requests (if more than one) */}
        {!requestsLoading && activeRequests.length > 1 && (
          <div className="space-y-2">
            <h3 className="ava-overline">Otras solicitudes activas</h3>
            {activeRequests.slice(1).map((req: any) => {
              const redirect = getRedirectPath(req);
              const statusConfig = getStatusConfig(req.status);
              return (
                <Card key={req.id} className="hover:shadow-sm transition-shadow cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" tabIndex={0} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') handleViewRequest(req); }} onClick={() => handleViewRequest(req)}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-1 h-10 rounded-full ${statusConfig.barColor}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <MonoText className="text-sm text-primary">{req.requestId}</MonoText>
                            <StatusBadge tone={statusConfig.tone}>{statusConfig.label}</StatusBadge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {req.clientName || redirect.stepName}
                          </p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">Continuar</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Completed requests */}
        {!requestsLoading && completedRequests.length > 0 && (
          <div className="space-y-2">
            <h3 className="ava-overline">Solicitudes finalizadas</h3>
            {completedRequests.map((req: any) => {
              const statusConfig = getStatusConfig(req.status);
              return (
                <Card key={req.id} className="hover:shadow-sm transition-shadow cursor-pointer opacity-80 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" tabIndex={0} onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') handleViewRequest(req); }} onClick={() => handleViewRequest(req)}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-1 h-10 rounded-full ${statusConfig.barColor}`} />
                        <div>
                          <div className="flex items-center gap-2">
                            <MonoText className="text-sm">{req.requestId}</MonoText>
                            <StatusBadge tone={statusConfig.tone}>{statusConfig.label}</StatusBadge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {req.clientName || (req.customerType === "PERSONA_JURIDICA" ? "Persona Jurídica" : "Persona Humana")}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}


        {/* Sin solicitudes — EmptyState DS (mock kit v2 HomePJScreen "Mis trámites") */}
        {!requestsLoading && !activeRequest && activeRequests.length === 0 && completedRequests.length === 0 && !isPJ && (
          <section>
            <div className="mb-3">
              <h3 className="ava-overline">Mis trámites</h3>
              <p className="text-xs text-muted-foreground mt-1">Solicitudes de onboarding iniciadas.</p>
            </div>
            <EmptyState
              icon={null}
              title={needsVerification ? "Todavía no tenés trámites." : "No tenés solicitudes de onboarding aún."}
              body={
                needsVerification ? (
                  "Verificá tu identidad para poder iniciar uno."
                ) : (
                  <>
                    Usá el botón <strong className="font-semibold text-foreground">Nueva solicitud</strong> para comenzar tu trámite de alta.
                  </>
                )
              }
            />
          </section>
        )}

        {/* Sin solicitudes PJ con empresas — EmptyState DS */}
        {!requestsLoading && !activeRequest && activeRequests.length === 0 && completedRequests.length === 0 && isPJ && companies && companies.length > 0 && (
          <section>
            <div className="mb-3">
              <h3 className="ava-overline">Mis trámites</h3>
              <p className="text-xs text-muted-foreground mt-1">Solicitudes de onboarding iniciadas.</p>
            </div>
            <EmptyState
              icon={null}
              title="No tenés solicitudes de onboarding aún."
              body={
                <>
                  Usá el botón <strong className="font-semibold text-foreground">Nueva solicitud</strong> y seleccioná una empresa para comenzar.
                </>
              }
            />
          </section>
        )}
      </main>

      {/* Dialog para seleccionar empresa al crear solicitud (PJ) */}
      <Dialog open={showNewRequestDialog} onOpenChange={setShowNewRequestDialog}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              Nueva solicitud
            </DialogTitle>
            <DialogDescription>
              Seleccioná la empresa para la cual querés iniciar el trámite de alta.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <CompanyManager
              onSelectCompany={(company) => setSelectedCompanyId(company.id)}
              selectedCompanyId={selectedCompanyId}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowNewRequestDialog(false); setSelectedCompanyId(null); }}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateRequestWithCompany}
              disabled={!selectedCompanyId || createMutation.isPending}
            >
              {createMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</>
              ) : (
                <><ArrowRight className="w-4 h-4 mr-2" />Iniciar Solicitud</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <footer className="border-t py-6 mt-8">
        <div className="container mx-auto px-6 text-center text-xs text-muted-foreground">
          <p>AVA Onboarding · © {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}
